export type UnknownRecord = Record<string, unknown>;

export function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function recordAt(value: unknown, ...keys: string[]): UnknownRecord | undefined {
  let current: unknown = value;
  for (const key of keys) {
    if (!isRecord(current)) return undefined;
    current = current[key];
  }
  return isRecord(current) ? current : undefined;
}

export function unwrapData(value: unknown): unknown {
  if (!isRecord(value)) return value;
  return "data" in value ? value.data : value;
}

export function listFrom(value: unknown, ...keys: string[]): UnknownRecord[] {
  const unwrapped = unwrapData(value);
  if (Array.isArray(unwrapped)) return unwrapped.filter(isRecord);
  if (!isRecord(unwrapped)) return [];
  for (const key of keys) {
    const candidate = unwrapped[key];
    if (Array.isArray(candidate)) return candidate.filter(isRecord);
  }
  const generic = unwrapped.items ?? unwrapped.results ?? unwrapped.entries;
  return Array.isArray(generic) ? generic.filter(isRecord) : [];
}

export function text(value: unknown, fallback = "—"): string {
  return typeof value === "string" && value.trim() ? value : fallback;
}

export function num(value: unknown, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

export function bool(value: unknown, fallback = false): boolean {
  return typeof value === "boolean" ? value : fallback;
}

export function idOf(value: UnknownRecord): string {
  return text(value.id ?? value.slug ?? value.gameId ?? value.tableId, "");
}

export function itemsMeta(value: unknown): { nextCursor?: string; total?: number } {
  const unwrapped = unwrapData(value);
  if (!isRecord(unwrapped)) return {};
  const meta = isRecord(unwrapped.meta) ? unwrapped.meta : unwrapped;
  const nextCursor = typeof meta.nextCursor === "string" ? meta.nextCursor : undefined;
  const total = typeof meta.total === "number" ? meta.total : undefined;
  return {
    ...(nextCursor ? { nextCursor } : {}),
    ...(total !== undefined ? { total } : {}),
  };
}
